import { gql, useQuery } from '@apollo/client';
import RemainingReservesDonutChartThumbnail from '@assets/images/report-components/reserves-base-case-donut-chart-thumbnail.png';
import { useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { GraphWrapper } from '@/admins/SmartReports/components/Components/Charts/GraphWrapper';
import { getLayout } from '@/admins/SmartReports/components/Components/Charts/helpers';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import {
  type ComponentDefinition,
  type ComponentEditProps,
  ComponentViewMode,
  type ComponentViewProps,
  type UseDataType,
} from '@/admins/SmartReports/components/Components/types';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { Asset } from '@/types/GraphqlSchema';
import { getNestedValue } from '@/utils/helpers';
import { formatTwoDecimals } from '@/utils/numberFormatter';

export const COMPONENT_ID = 'remaining_reserves_base_case_donut';

// we need the ID for Apollo to cache properly
const QUERY = gql`
  query assetV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id

      reserve {
        pdpOilMmbbl
        pdpGasMmboe
        pdpCondensateMmbbl

        undevelopedOilMmbbl
        undevelopedGasMmboe
        undevelopedCondensateMmbbl
      }
    }
  }
`;

// Define enum for reserve types
enum ReserveType {
  PDP_OIL = 'PDP - Oil',
  PDP_GAS = 'PDP - Gas',
  PDP_CONDENSATE = 'PDP - Condensate',
  UNDEVELOPED_OIL = 'Undeveloped - Oil',
  UNDEVELOPED_GAS = 'Undeveloped - Gas',
  UNDEVELOPED_CONDENSATE = 'Undeveloped - Condensate',
}

// Define colors for each reserve type
const COLOR_MAP = {
  [ReserveType.PDP_OIL]: 'palette.chartColors.earthGreen.c100',
  [ReserveType.PDP_GAS]: 'palette.chartColors.trueRed.c100',
  [ReserveType.PDP_CONDENSATE]: 'palette.chartColors.amber.c100',
  [ReserveType.UNDEVELOPED_OIL]: 'palette.chartColors.aqua.c100',
  [ReserveType.UNDEVELOPED_GAS]: 'palette.chartColors.trueRed.c50',
  [ReserveType.UNDEVELOPED_CONDENSATE]: 'palette.chartColors.amber.c50',
};

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<Asset>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const assetId = portfolio?.assets?.[0];
  const { data, loading, error } = useQuery<{ assetV2: Asset }>(QUERY, {
    variables: { input: { id: assetId } },
    skip: !!persistedData,
  });

  useEffect(() => {
    if (data?.assetV2) {
      const chartData = data.assetV2;
      setChartDataFetched(chartData);
      if (onComponentLoad) {
        onComponentLoad({
          componentId: COMPONENT_ID,
          data: chartData,
        });
      }
    }
  }, [data, onComponentLoad]);

  return { chartData: chartDataFetched, error, loading };
};

const ComponentView = ({ data, mode = ComponentViewMode.View }: ComponentViewProps<Asset>) => {
  const theme = useTheme();
  const formattedData = useMemo(() => {
    if (!data) return null;

    const reserves = {
      [ReserveType.UNDEVELOPED_OIL]: data.reserve?.undevelopedOilMmbbl || 0,
      [ReserveType.UNDEVELOPED_GAS]: data.reserve?.undevelopedGasMmboe || 0,
      [ReserveType.UNDEVELOPED_CONDENSATE]: data.reserve?.undevelopedCondensateMmbbl || 0,
      [ReserveType.PDP_OIL]: data.reserve?.pdpOilMmbbl || 0,
      [ReserveType.PDP_GAS]: data.reserve?.pdpGasMmboe || 0,
      [ReserveType.PDP_CONDENSATE]: data.reserve?.pdpCondensateMmbbl || 0,
    };

    const colors = Object.keys(reserves).map(label =>
      getNestedValue(theme, COLOR_MAP[label as ReserveType]),
    );
    // Filter out zero values and create pie chart data
    const pieData = [
      {
        labels: Object.keys(reserves), // Use the order of keys in the reserves object
        values: Object.values(reserves).map(v => Number(formatTwoDecimals(v))),
        type: 'pie',
        hovertemplate: `%{label}<br>%{value:,.2f} MMboe<br>%{percent}<extra></extra>`,
        textinfo: 'value',
        textposition: 'inside',
        hole: 0.6, // Creates donut chart with white center
        marker: { colors },
        textfont: {
          size: 18, // Increase font size for text inside the pie chart
        },
        sort: false, // Prevent Plotly from sorting by value
      },
    ];

    // Calculate total reserves for center display
    const totalReserves = Object.values(reserves).reduce((acc, value) => acc + value, 0);

    return {
      pieData,
      totalReserves,
    };
  }, [data, theme]);

  if (!formattedData) return null;

  const { pieData, totalReserves } = formattedData;

  const baseLayout = getLayout({ font: { size: 12 }, legend: { y: -0.2 } });

  return (
    <ComponentBox>
      <ComponentTitle>Remaining Reserves Base Case</ComponentTitle>
      <GraphWrapper
        data={pieData}
        layout={{
          ...baseLayout,
          annotations: [
            ...(baseLayout.annotations || []),
            {
              text: `${formatTwoDecimals(totalReserves)}`,
              x: 0.5,
              y: 0.5,
              xref: 'paper',
              yref: 'paper',
              showarrow: false,
              font: {
                size: 24,
                color: '#000000',
                family: 'Inter, sans-serif',
              },
              align: 'center',
            },
          ],
        }}
        config={{
          staticPlot: mode !== ComponentViewMode.View,
        }}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<Asset>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} mode={ComponentViewMode.Edit} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      Remaining Reserves Base Case
    </ComponentTitle>
    <img
      src={RemainingReservesDonutChartThumbnail}
      alt='Remaining Reserves Base Case Donut Chart Thumbnail'
      style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
    />
  </ComponentBox>
);

export const RemainingReservesBaseCaseDonutChartComponent: ComponentDefinition<Asset> = {
  id: COMPONENT_ID,
  name: 'Remaining Reserves Base Case',
  type: SmartReportComponentTypesEnum.Chart,
  reportTypes: [SmartReportTypeEnum.Asset],
  Thumbnail: ComponentThumbnail,
  Component: ComponentEdit,
  ComponentView,
};
